'use client';

import { trackAffiliateClick } from '@/lib/analytics';
import { ensureBrowserSignalCookie } from '@/lib/tracking/browser-signal-cookie';
import { track } from '@/lib/tracking/tracker';
import { openExternalViaShell } from '@/lib/native/open-external';
import { rememberViewedProduct, storeLabelForLink } from '@/lib/viewed-products';

interface AffiliateLinkTrackerProps {
  href: string;
  productTitle: string;
  isAmazon: boolean;
  locale: string;
  region: string;
  index: number;
  /** Amazon ASIN when known — lets click events join the products table. */
  asin?: string;
  /** Category page id the product sits on — ties buying signals to a category for profile learning. */
  categoryId?: string;
  className: string;
  /** Inline styles for the anchor. Exists for the offers row, whose height has
   *  to come from the same px constant `panelHeight()` multiplies rather than
   *  from a rem class the root font-size rescales per breakpoint. */
  style?: React.CSSProperties;
  children: React.ReactNode;
  onClick?: () => void;
  /** Descriptive label for screen readers and AI scrapers describing what the link does. */
  ariaLabel?: string;
  /** Native title attribute (tooltip) — also picked up by AI parsers as link intent. */
  title?: string;
  /**
   * Schema.org microdata property name for this link. When this link sits inside
   * an Offer scope, set itemProp="url" so AI parsers attach the purchase URL to
   * the Offer (linking the visible CTA to the structured price/availability data).
   */
  itemProp?: string;
  /**
   * Semantic data-attribute identifying the role of this CTA (e.g.
   * 'purchase-cta', 'price-comparison'). Surfaces intent to AI/SEO crawlers
   * that don't parse microdata.
   */
  dataPurpose?: string;
  /**
   * Extra dimensions merged into the `click_affiliate` event.
   *
   * Exists for the offers panel, where a single product now has several buy
   * links: without `{merchant_domain, offer_index, offer_kind}` every row
   * reports as the same undifferentiated click and there is no way to tell
   * which retailer the reader actually chose. Spread LAST so a caller cannot
   * accidentally shadow product_title / link_type / product_index.
   */
  extraTrackProps?: Record<string, unknown>;
}

/**
 * Client component for tracking affiliate link clicks
 * Separated from server component for optimal performance
 */
export function AffiliateLinkTracker({
  href,
  productTitle,
  isAmazon,
  locale,
  region,
  index,
  asin,
  categoryId,
  className,
  style,
  children,
  onClick,
  ariaLabel,
  title,
  itemProp,
  dataPurpose,
  extraTrackProps,
}: AffiliateLinkTrackerProps) {
  // Handle affiliate link click tracking
  const handleAffiliateClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const linkType = isAmazon ? 'amazon' : 'external';
    const pageUrl = typeof window !== 'undefined' ? window.location.href : '';

    // `href` points at /go, which attaches the partner tag only when the
    // navigation carries the browser-signal cookie. The tracker writes it on
    // mount; re-asserting it here, synchronously before the navigation the
    // click starts, means a cleared cookie jar or a layout without the
    // tracker cannot cost the commission.
    ensureBrowserSignalCookie();

    // Dual-write while GA4 and the first-party pipeline run side by side.
    trackAffiliateClick(productTitle, linkType, pageUrl, locale, region, index);
    track('click_affiliate', {
      product_title: productTitle,
      link_type: linkType,
      product_index: index,
      ...(asin ? { asin } : {}),
      ...(categoryId ? { category_id: categoryId } : {}),
      ...(extraTrackProps ?? {}),
    });

    // The profile's "Products you opened" list, kept in this browser only.
    // Scoped by the page's full locale (`en-CA`, first path segment), the way
    // the history stores are; `locale` here is the bare language code.
    // Best effort: a full or blocked localStorage must never cost the click.
    try {
      const pathLocale = typeof window !== 'undefined' ? window.location.pathname.split('/')[1] : '';
      rememberViewedProduct({
        title: productTitle,
        store: storeLabelForLink(href, isAmazon, extraTrackProps?.merchant_domain),
        pageUrl: pageUrl || undefined,
        locale: /^[a-z]{2}-[A-Z]{2}$/.test(pathLocale) ? pathLocale : locale,
      });
    } catch {
      // ignore
    }

    // Call additional onClick handler if provided
    if (onClick) {
      onClick();
    }

    // In the native app shell, target="_blank" is a dead end and Amazon must
    // open in the external browser (Associates Mobile Application Policy) —
    // hand the URL to the shell AFTER the tracking calls above have fired.
    if (openExternalViaShell(href)) {
      e.preventDefault();
    }
  };

  return (
    <a
      href={href}
      rel="sponsored nofollow"
      target="_blank"
      onClick={handleAffiliateClick}
      className={className}
      style={style}
      aria-label={ariaLabel}
      title={title}
      itemProp={itemProp}
      data-purpose={dataPurpose}
    >
      {children}
    </a>
  );
}
